import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { uploadFile, uploadMultipleFiles, downloadFile, getFileUrl } from '../../api/file';

// 单文件上传异步操作
export const uploadFileAsync = createAsyncThunk(
  'file/uploadFile',
  async ({ file, directory, keepOriginalName, checkDuplicate }, { rejectWithValue }) => {
    try {
      const response = await uploadFile(file, directory, keepOriginalName, checkDuplicate);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// 多文件上传异步操作
export const uploadMultipleFilesAsync = createAsyncThunk(
  'file/uploadMultipleFiles',
  async ({ files, directory, keepOriginalName, checkDuplicate }, { rejectWithValue }) => {
    try {
      const response = await uploadMultipleFiles(files, directory, keepOriginalName, checkDuplicate);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// 文件下载异步操作
export const downloadFileAsync = createAsyncThunk(
  'file/downloadFile',
  async (objectName, { rejectWithValue }) => {
    try {
      const response = await downloadFile(objectName);
      // 创建下载链接
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', objectName.split('/').pop());
      document.body.appendChild(link);
      link.click();
      link.remove();
      return objectName;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// 获取文件临时URL异步操作
export const getFileUrlAsync = createAsyncThunk(
  'file/getFileUrl',
  async ({ objectName, expireTime = 3600 }, { rejectWithValue }) => {
    try {
      const response = await getFileUrl(objectName, expireTime);
      return {
        objectName,
        url: response.data
      };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

const fileSlice = createSlice({
  name: 'fileUpload',
  initialState: {
    uploadedFiles: [],
    downloadedFiles: [],
    fileUrls: {}, // 新增：存储文件临时URL的对象
    loading: false,
    error: null
  },
  reducers: {
    // 清除特定文件的临时URL
    clearFileUrl: (state, action) => {
      const objectName = action.payload;
      delete state.fileUrls[objectName];
    }
  },
  extraReducers: (builder) => {
    builder
      // 单文件上传处理
      .addCase(uploadFileAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadFileAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.uploadedFiles.push(action.payload);
      })
      .addCase(uploadFileAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // 多文件上传处理
      .addCase(uploadMultipleFilesAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadMultipleFilesAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.uploadedFiles = [...state.uploadedFiles, ...action.payload];
      })
      .addCase(uploadMultipleFilesAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // 文件下载处理
      .addCase(downloadFileAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(downloadFileAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.downloadedFiles.push(action.payload);
      })
      .addCase(downloadFileAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // 获取文件临时URL处理
      .addCase(getFileUrlAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getFileUrlAsync.fulfilled, (state, action) => {
        state.loading = false;
        const { objectName, url } = action.payload;
        state.fileUrls[objectName] = {
          url,
          timestamp: Date.now()
        };
      })
      .addCase(getFileUrlAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

// 导出 action creators
export const { clearFileUrl } = fileSlice.actions;

export default fileSlice.reducer;
